import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, notification } from "antd";
import Button from "../../../commons/Button/Button";
import Spinner from "../../../commons/Spinner/Spinner";
import { FormField } from "../../../commons/FormField/FormField";
import { DropdownWithSearch } from "../../../commons/Dropdown/DropdownWithSearch";
import useNotificationTypes from "../../../state/hooks/notifications-management/useNotificationTypes";
import useNotificationKeysValues from "../../../state/hooks/notifications-management/useNotificationKeysValues";
import useSendNotification from "../../../state/hooks/notifications-management/useSendNotification";
import {
  BASE_LANGUAGE,
  EMAIL_REGEX,
  MAX_RECIPIENTS,
  NO_FCM_MATCH_MESSAGE,
  RECIPIENT_OWN_LANGUAGE,
  SEND_LANGUAGE_OPTIONS,
} from "../constants";
import { countMissingValues, getApiErrorMessage } from "../helpers";
import { CountBadge } from "./NotificationCells";
import PushPreview from "./PushPreview";
import RecipientPicker from "./RecipientPicker";

const SendNotificationPanel = () => {
  const [notificationType, setNotificationType] = useState<string>("");
  const [variant, setVariant] = useState<number | null>(null);
  const [sendLanguage, setSendLanguage] = useState<string>(
    RECIPIENT_OWN_LANGUAGE
  );
  const [emails, setEmails] = useState<string[]>([]);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isLanguageForced = sendLanguage !== RECIPIENT_OWN_LANGUAGE;
  /** What the preview resolves against: the forced language, or the base one. */
  const previewLanguage = isLanguageForced ? sendLanguage : BASE_LANGUAGE;

  const {
    data: typesData,
    isLoading: isLoadingTypes,
    error: typesError,
  } = useNotificationTypes();

  // Variant options live in keys-values (the types endpoint lists each type once).
  const { data: keysValuesData, isFetching: isLoadingVariants } =
    useNotificationKeysValues(
      {
        interfaceLanguage: previewLanguage,
        notificationType: notificationType || undefined,
      },
      !!notificationType
    );

  const { mutate: sendNotification, isPending } = useSendNotification();

  const typeOptions = useMemo(
    () =>
      (typesData?.notificationTypes ?? []).map((type) => ({
        value: type,
        label: type,
      })),
    [typesData?.notificationTypes]
  );

  const keySets = useMemo(() => {
    if (!notificationType) return [];
    return (keysValuesData?.keysValues ?? [])
      .filter((entry) => entry.notificationType === notificationType)
      .sort((a, b) => a.variant - b.variant);
  }, [keysValuesData?.keysValues, notificationType]);

  const hasMultipleKeySets = keySets.length > 1;

  // Reset the variant whenever the type changes, then default to the lowest.
  useEffect(() => {
    setVariant(null);
  }, [notificationType]);

  useEffect(() => {
    if (!hasMultipleKeySets) return;
    setVariant((current) =>
      current !== null && keySets.some((entry) => entry.variant === current)
        ? current
        : keySets[0].variant
    );
  }, [hasMultipleKeySets, keySets]);

  useEffect(() => {
    if (!typesError) return;
    notification.error({
      key: "notification-types-load-error",
      message: "Error",
      description: getApiErrorMessage(
        typesError,
        "Failed to load notification types."
      ),
      placement: "top",
    });
  }, [typesError]);

  const selectedKeySet = useMemo(
    () =>
      keySets.find((entry) => entry.variant === variant) ??
      (keySets.length === 1 ? keySets[0] : undefined),
    [keySets, variant]
  );

  const missingInPreview = selectedKeySet
    ? countMissingValues(selectedKeySet)
    : 0;

  const invalidEmails = emails.filter((email) => !EMAIL_REGEX.test(email));
  const isOverCap = emails.length > MAX_RECIPIENTS;

  const canSubmit =
    !!notificationType &&
    emails.length > 0 &&
    invalidEmails.length === 0 &&
    !isOverCap &&
    !isPending;

  const handleEmailsChange = (values: unknown[]) => {
    const seen = new Set<string>();
    const next: string[] = [];

    values
      .map((value) => String(value).trim())
      .filter(Boolean)
      .forEach((email) => {
        const dedupeKey = email.toLowerCase();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        next.push(email);
      });

    setEmails(next);
  };

  const handleAddEmail = (email: string) => {
    setEmails((current) => {
      if (current.length >= MAX_RECIPIENTS) return current;
      if (current.some((item) => item.toLowerCase() === email.toLowerCase())) {
        return current;
      }
      return [...current, email];
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    sendNotification(
      {
        notificationType,
        emails,
        // Omitted for single-key-set types — the backend sends the lowest variant.
        ...(hasMultipleKeySets && variant !== null ? { variant } : {}),
        // Omitted unless forced — the backend then uses each recipient's language.
        ...(isLanguageForced ? { interfaceLanguage: sendLanguage } : {}),
      },
      {
        onSuccess: (response) => {
          notification.success({
            message: "Notifications Queued",
            description: (
              <span>
                {response.message}
                <br />
                Key set sent: variant {response.variant} · queued{" "}
                {response.totalQueued}
              </span>
            ),
            placement: "top",
            duration: 6,
          });
          setEmails([]);
        },
        onError: (error) => {
          const description = getApiErrorMessage(
            error,
            "Failed to queue the push notification."
          );

          if (
            description
              .toLowerCase()
              .includes(NO_FCM_MATCH_MESSAGE.toLowerCase())
          ) {
            notification.warning({
              message: "Nothing Queued",
              description,
              placement: "top",
            });
            return;
          }

          notification.error({
            message: "Send Failed",
            description,
            placement: "top",
          });
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-20 w-full min-w-0">
      <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0">
        <h3 className="text-dark-grey font-bold text-lg mb-8">
          Send Push Notification
        </h3>
        <p className="text-sm text-dark-grey mb-30" style={{ opacity: 0.6 }}>
          Each recipient gets the push in their own interface language unless
          you force one below. Unknown emails and users without an FCM token are
          skipped silently.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-30 items-start">
          <div className="min-w-0">
            <FormField label="Notification type" col>
              {isLoadingTypes ? (
                <div className="flex items-center gap-2">
                  <Spinner />
                  <span
                    className="text-sm text-dark-grey"
                    style={{ opacity: 0.5 }}
                  >
                    Loading types...
                  </span>
                </div>
              ) : (
                <DropdownWithSearch
                  options={typeOptions}
                  placeholder="Select a notification type..."
                  value={notificationType}
                  onChange={(option) => setNotificationType(option?.value ?? "")}
                  className="w-full"
                  id="notification-send-type"
                />
              )}
            </FormField>

            {hasMultipleKeySets && (
              <FormField label="Key set (variant)" col>
                <DropdownWithSearch
                  options={keySets.map((entry) => ({
                    value: String(entry.variant),
                    label: `Variant ${entry.variant}${
                      entry.title ? ` — ${entry.title}` : ""
                    }`,
                  }))}
                  placeholder="Select a key set..."
                  value={variant !== null ? String(variant) : undefined}
                  onChange={(option) =>
                    setVariant(option ? Number(option.value) : null)
                  }
                  className="w-full"
                  id="notification-send-variant"
                />
                <p className="text-xs mt-8" style={{ color: "#6b7280" }}>
                  This type owns {keySets.length} key sets — the job normally
                  picks one per user, here you choose it.
                </p>
              </FormField>
            )}

            <FormField label="Interface language" col>
              <DropdownWithSearch
                options={SEND_LANGUAGE_OPTIONS}
                placeholder="Recipient's own language (default)"
                value={sendLanguage}
                onChange={(option) =>
                  setSendLanguage(option?.value ?? RECIPIENT_OWN_LANGUAGE)
                }
                className="w-full"
                id="notification-send-language"
              />
              <p className="text-xs mt-8" style={{ color: "#6b7280" }}>
                {isLanguageForced
                  ? `Every recipient receives the ${sendLanguage} copy, whatever language their account is set to.`
                  : "Each recipient receives the copy for their own account language."}
              </p>
            </FormField>

            <FormField label="Recipients" col>
              <Select
                mode="tags"
                value={emails}
                onChange={handleEmailsChange}
                tokenSeparators={[",", " ", ";", "\n"]}
                placeholder="Type or paste emails, then press Enter"
                className="w-full"
                style={{ minHeight: 48 }}
                open={false}
                suffixIcon={null}
                id="notification-send-emails"
              />
              <div className="flex items-center gap-10 mt-8 flex-wrap">
                <CountBadge tone={isOverCap ? "error" : "neutral"}>
                  {emails.length}/{MAX_RECIPIENTS} recipients
                </CountBadge>
                <span className="text-xs" style={{ color: "#6b7280" }}>
                  Separate with Enter, comma, or space — or add them from the
                  list below.
                </span>
                {emails.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEmails([])}
                    className="text-xs font-semibold cursor-pointer"
                    style={{
                      color: "#2d3ac7",
                      background: "transparent",
                      border: "none",
                      padding: 0,
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {isOverCap && (
                <p className="text-error text-sm mt-8">
                  A single send accepts at most {MAX_RECIPIENTS} addresses —
                  remove {emails.length - MAX_RECIPIENTS}.
                </p>
              )}
              {invalidEmails.length > 0 && (
                <p className="text-error text-sm mt-8">
                  Invalid email{invalidEmails.length > 1 ? "s" : ""}:{" "}
                  {invalidEmails.join(", ")}
                </p>
              )}
            </FormField>

            <div className="flex items-center gap-20 flex-wrap">
              <Button
                type="main"
                onClick={() => setConfirmVisible(true)}
                disabled={!canSubmit}
                isLoading={isPending}
              >
                Send Push
              </Button>
              <span className="text-xs" style={{ color: "#9CA3AF" }}>
                Queued per recipient — a send cannot be recalled.
              </span>
            </div>
          </div>

          <div className="min-w-0 flex flex-col gap-10">
            <div className="flex items-center justify-between gap-10 flex-wrap">
              <span className="text-dark-grey font-bold text-sm">Preview</span>
              <div className="flex items-center gap-10 flex-wrap">
                {selectedKeySet && (
                  <CountBadge tone="accent">
                    Variant {selectedKeySet.variant}
                  </CountBadge>
                )}
                <CountBadge tone={isLanguageForced ? "accent" : "neutral"}>
                  {previewLanguage}
                </CountBadge>
                {missingInPreview > 0 && (
                  <CountBadge tone="error">
                    {missingInPreview} missing
                  </CountBadge>
                )}
              </div>
            </div>

            {notificationType && isLoadingVariants && !selectedKeySet ? (
              <div
                className="flex items-center justify-center gap-10 rounded-xl"
                style={{
                  border: "1px dashed #D5D5D5",
                  background: "#FCFCFE",
                  minHeight: 220,
                }}
              >
                <Spinner />
                <span
                  className="text-sm text-dark-grey"
                  style={{ opacity: 0.5 }}
                >
                  Loading key sets...
                </span>
              </div>
            ) : (
              <PushPreview entry={selectedKeySet} />
            )}

            {missingInPreview > 0 && (
              <p className="text-error text-xs m-0 font-semibold">
                {missingInPreview} key{missingInPreview === 1 ? "" : "s"} missing
                in {previewLanguage} — that part of the push will arrive empty.
              </p>
            )}

            <p className="text-xs m-0" style={{ color: "#9CA3AF" }}>
              {isLanguageForced
                ? `Forced to ${previewLanguage} — this exact copy goes to every recipient.`
                : `Rendered in ${previewLanguage}. Every recipient receives it in their own interface language — check the others on the Localized Preview tab.`}
            </p>
          </div>
        </div>
      </div>

      <RecipientPicker selectedEmails={emails} onAdd={handleAddEmail} />

      <Modal
        title="Confirm Send"
        open={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <div className="flex justify-end gap-10 mt-20">
            <Button
              type="secondary"
              border
              onClick={() => setConfirmVisible(false)}
              disabled={isPending}
            >
              <span className="whitespace-nowrap">Cancel</span>
            </Button>
            <Button
              type="main"
              isLoading={isPending}
              disabled={isPending}
              onClick={() => {
                setConfirmVisible(false);
                handleSubmit();
              }}
            >
              <span className="whitespace-nowrap">Confirm &amp; Send</span>
            </Button>
          </div>
        }
      >
        <p className="text-sm text-dark-grey" style={{ marginBottom: 16 }}>
          Send <strong>{notificationType || "—"}</strong>
          {hasMultipleKeySets && variant !== null ? (
            <>
              {" "}
              variant <strong>{variant}</strong>
            </>
          ) : (
            " (lowest variant)"
          )}{" "}
          to <strong>{emails.length}</strong> recipient
          {emails.length === 1 ? "" : "s"}
          {isLanguageForced ? (
            <>
              {" "}
              in <strong>{sendLanguage}</strong>?
            </>
          ) : (
            " in their own language?"
          )}
        </p>
        <p className="text-sm" style={{ color: "#6b7280", marginBottom: 8 }}>
          Pushes are queued per recipient and cannot be recalled. Unknown emails
          and users without an FCM token are skipped.
        </p>
        <div
          className="rounded-md px-20 py-10 text-sm"
          style={{
            background: "#F8F9FC",
            color: "#6b7280",
            maxHeight: 160,
            overflowY: "auto",
            wordBreak: "break-word",
          }}
        >
          {emails.join(", ")}
        </div>
      </Modal>
    </div>
  );
};

export default SendNotificationPanel;
